import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Download, FileText, Loader2, AlertCircle } from 'lucide-react';

interface FileInfo {
  id: string;
  file_name: string;
  file_path: string;
  content_type: string | null;
  file_size: number | null;
  og_image_path: string | null;
  landing_title: string | null;
  landing_description: string | null;
}

export default function FileDownload() {
  const { slug } = useParams<{ slug: string }>();
  const [file, setFile] = useState<FileInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setError('Brak identyfikatora pliku');
      setLoading(false);
      return;
    }

    const fetchFile = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('file_uploads')
          .select('id, file_name, file_path, content_type, file_size, og_image_path, landing_title, landing_description')
          .eq('vanity_slug', slug)
          .single();

        if (fetchError || !data) {
          setError('Plik nie został znaleziony');
          return;
        }

        setFile(data);

        // Increment view count
        await supabase.rpc('increment_file_view_count', { file_slug: slug });
      } catch (err) {
        setError('Wystąpił błąd podczas ładowania pliku');
      } finally {
        setLoading(false);
      }
    };

    fetchFile();
  }, [slug]);

  const formatFileSize = (bytes: number | null): string => {
    if (!bytes) return 'Nieznany rozmiar';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getDownloadUrl = (): string => {
    if (!file?.file_path) return '';
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    // Remove any accidental 'file-uploads/' prefix from stored path
    const cleanPath = file.file_path.replace(/^file-uploads\//, '');
    return `${supabaseUrl}/storage/v1/object/public/file-uploads/${cleanPath}`;
  };

  const getOgImageUrl = (): string | null => {
    if (!file?.og_image_path) return null;
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    // Remove any accidental 'file-uploads/' prefix from stored path
    const cleanPath = file.og_image_path.replace(/^file-uploads\//, '');
    return `${supabaseUrl}/storage/v1/object/public/file-uploads/${cleanPath}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="flex items-center gap-3 text-white">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span>Ładowanie...</span>
        </div>
      </div>
    );
  }

  if (error || !file) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800/90 border-slate-700 backdrop-blur-lg">
          <CardContent className="pt-8 pb-8 text-center">
            <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-white mb-2">Nie znaleziono pliku</h1>
            <p className="text-slate-400 mb-6">{error || 'Plik nie istnieje lub link wygasł'}</p>
            <Button asChild variant="outline" className="border-slate-600 text-slate-200 hover:bg-slate-700">
              <Link to="/">Wróć na stronę główną</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const ogImageUrl = getOgImageUrl();
  const pageTitle = file.landing_title || file.file_name;
  const pageDescription = file.landing_description || `Pobierz: ${file.file_name}`;

  return (
    <>
      <Helmet>
        <title>{pageTitle} | LIFactor</title>
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        {ogImageUrl && <meta property="og:image" content={ogImageUrl} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {ogImageUrl && <meta name="twitter:image" content={ogImageUrl} />}
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800/90 border-slate-700 backdrop-blur-lg shadow-2xl">
        <CardContent className="pt-10 pb-10 text-center">
          {ogImageUrl ? (
            <div className="mb-7">
              <img 
                src={ogImageUrl} 
                alt={file.file_name}
                className="max-h-44 max-w-full mx-auto object-contain rounded-xl border border-slate-600"
              />
            </div>
          ) : (
            <div className="w-20 h-20 mx-auto mb-7 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center">
              <FileText className="h-10 w-10 text-white" />
            </div>
          )}
          
          <h1 className="text-xl font-semibold text-white mb-3 break-words leading-tight">
            {file.file_name}
          </h1>
          
          <p className="text-slate-400 text-sm mb-7">
            {formatFileSize(file.file_size)}
            {file.content_type && ` • ${file.content_type}`}
          </p>
          
          <Button 
            asChild
            size="lg"
            className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg shadow-blue-500/30"
          >
            <a href={getDownloadUrl()} download={file.file_name}>
              <Download className="mr-2 h-5 w-5" />
              Pobierz plik
            </a>
          </Button>
          
          <p className="mt-6 text-xs text-slate-500">
            Plik udostępniony przez{' '}
            <a href="https://lifactor.com" target="_blank" rel="noopener" className="text-blue-400 hover:underline">
              LIFactor
            </a>
          </p>
        </CardContent>
      </Card>
    </div>
    </>
  );
}
